import { useEffect, useMemo, useState } from 'react';
import { api, isErr, type Entity, type Meta, type QueryResult, type TagCounts } from '../lib/api.ts';
import { Ask } from './Ask.tsx';

/* The Data grid. Three endpoints, all ported: /api/meta, /api/query, /api/tagcounts.
 *
 * The whole surface is GENERATED FROM THE REGISTRY. 116 columns arrive from /api/meta carrying
 * type, label, width, group and a provenance string, so "every column filterable at every level" is
 * one data structure rather than 116 hand-written widgets. Adding a column to the snapshot makes it
 * appear here with a working filter and no frontend change.
 */

const N = (v: unknown) => (v == null || v === '' ? '' : Number(v).toLocaleString('en-US'));
const ENTITIES: [Entity, string][] = [['acct', 'Accounts'], ['fac', 'Plants'], ['ppl', 'Contacts']];

interface Cond { f: string; o: string; v: string }

export function Grid({ meta, inject, onInjected, onOpenRow, onOpenInAtlas }: {
  meta: Meta;
  /* A filter handed over from the Manager or Engine room gap ledger. It arrives as the SAME AST the
     rail produces, so an opened gap is an ordinary filter the user can then edit — not a special
     read-only mode that has to be escaped. */
  inject?: { ent: Entity; q: unknown } | null;
  onInjected?: () => void;
  onOpenRow?: (entity: Entity, id: string | number) => void;
  onOpenInAtlas?: (p: { f: string; o: string; v: string; label: string }) => void;
}) {
  const [ent, setEnt] = useState<Entity>('acct');
  const [conds, setConds] = useState<Cond[]>([]);
  const [chip, setChip] = useState<string | null>(null);
  /* A raw AST that the rail cannot express as condition rows. It is shown as a clearable chip
     rather than applied invisibly — a grid silently filtered to 41 rows with no visible reason is
     the same defect as the Atlas pin. */
  const [raw, setRaw] = useState<{ q: unknown; label: string } | null>(null);
  const [colq, setColq] = useState('');
  const [res, setRes] = useState<QueryResult | null>(null);
  const [tags, setTags] = useState<TagCounts | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /* Ranking, set only by the Ask bar today. A superlative ("the companies with the most plants") is
     a SORT, not a filter — inventing a threshold to turn it into one would be a made-up number in a
     client deliverable. null means "let the server apply its own default for this entity", which is
     why it is not seeded with DEFAULT_SORT here: two copies of that default would drift. */
  const [sort, setSort] = useState<{ k: string; d: 'asc' | 'desc' } | null>(null);

  const fields = meta.fields[ent];
  /* WHICH COLUMNS ARE ON SCREEN. It was `fields.slice(0, 9)` — a hardcoded first nine out of 116,
     with no way to reach the other 107. On 2026-08-07 a reviewer looking for a company's headcount
     concluded the dataset did not carry one. It did: `gmr_employees`, "Employees (whole company)",
     straight off the client's own SOM sheet — it was just past the cut, with no picker to reach it.
     A registry-driven grid whose registry is unreachable is a hardcoded grid, and a column nobody
     can find is indistinguishable from a column that does not exist.
     Per entity, remembered, and it falls back to the first nine on a first visit. */
  const [picked, setPicked] = useState<Record<string, string[]>>(() => {
    try { return JSON.parse(localStorage.getItem('atlas-grid-cols') || '{}') || {}; } catch { return {}; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => { localStorage.setItem('atlas-grid-cols', JSON.stringify(picked)); }, [picked]);
  const cols = useMemo(() => {
    const keys = picked[ent];
    if (!keys || !keys.length) return fields.slice(0, 9);
    // Resolved against the registry every render, so a column dropped from the snapshot disappears
    // rather than rendering as a blank column nobody can explain.
    return keys.map((k) => fields.find((f) => f.k === k)).filter(Boolean) as typeof fields;
  }, [fields, picked, ent]);
  const toggleCol = (k: string) => {
    const cur = picked[ent] && picked[ent].length ? picked[ent] : fields.slice(0, 9).map((f) => f.k);
    setPicked({ ...picked, [ent]: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] });
  };

  /* The filter the server compiles. A chip and typed conditions AND together, which is what the
     tag counts are measured against — a chip's number is "how many rows would this give me on top
     of what is already filtered", not a global total. */
  const q = useMemo(() => {
    const kids: unknown[] = [];
    if (raw) kids.push(raw.q);
    if (chip) kids.push({ tag: chip });
    for (const c of conds) if (c.f && c.o) kids.push({ f: c.f, o: c.o, v: c.v });
    return kids.length ? JSON.stringify({ op: 'AND', kids }) : '';
  }, [conds, chip, raw]);

  /* Applied ONCE per hand-off, then acknowledged, so switching tabs and coming back does not
     silently re-apply a gap filter the user has already cleared. */
  useEffect(() => {
    if (!inject) return;
    setEnt(inject.ent); setConds([]); setChip(null);
    setRaw({ q: inject.q, label: 'the rows behind a gap' });
    onInjected?.();
  }, [inject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let dead = false;
    setBusy(true); setErr(null);
    const qs = `e=${ent}&limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`
      + (sort ? `&sort=${encodeURIComponent(sort.k)}&dir=${sort.d}` : '');

    /* Two requests, deliberately not one. Thirteen SUM(CASE) over the contacts table is a full scan
       — measured at 1,007ms against 9ms for the page of rows beside it. Folded together, every
       keystroke would feel broken to save one round trip. The grid paints on the fast query and the
       chips fill in a beat later. */
    api<QueryResult>(`query?${qs}`).then((r) => {
      if (dead) return;
      setBusy(false);
      if (isErr(r)) { setErr(r.error); setRes(null); return; }
      setRes(r);
    });
    api<TagCounts>(`tagcounts?${qs}`).then((t) => { if (!dead) setTags(isErr(t) ? null : t); });

    return () => { dead = true; };
  }, [ent, q, sort]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* The Ask bar spans the whole view rather than sitting in the 264px rail: it takes a
          sentence, and a sentence does not fit in a rail. It is above BOTH the rail and the table
          because its answer changes the entity, which is the rail's first control. */}
      <Ask onResult={({ ent: e, q: ast, label, sort: s, dir }) => {
        setEnt(e); setConds([]); setChip(null);
        setRaw(ast ? { q: ast, label } : null);
        setSort(s ? { k: s, d: dir } : null);
      }} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

      {/* ── rail ─────────────────────────────────────────────────────────────────────────────
          One continuous panel, not floating cards. Section breaks are hairlines and every label
          sits on the same left edge (criteria B1, B2). Controls are visible by default: a set of new
          filters was once collapsed to look tidy, and the reviewer reported that nothing had
          changed — a control you cannot see does not exist. */}
      <aside style={{ width: 264, flex: '0 0 264px', borderRight: 'var(--hair)', overflowY: 'auto' }}>

        <Section label="Show me">
          <div style={{ display: 'flex', gap: 2 }}>
            {ENTITIES.map(([id, label]) => (
              <button
                key={id}
                onClick={() => { setEnt(id); setConds([]); setChip(null); setRaw(null); }}
                style={{
                  flex: 1, padding: '6px 0', cursor: 'pointer',
                  fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
                  border: 'var(--hair)', background: ent === id ? 'rgba(255,170,0,.043)' : 'transparent',
                  color: ent === id ? '#FFF' : 'var(--dim)',
                  // A border, not a box-shadow — the Atlas reset strips shadows. See ui.tsx Btn.
                  borderBottom: ent === id ? '2px solid var(--or)' : '2px solid transparent',
                }}
              >{label}</button>
            ))}
          </div>
          {/* The hand-off chip. A filter the rail cannot draw as condition rows must still be
              visible and clearable, or the grid is showing a narrowed set for a reason nobody on
              screen can see — the same defect the Atlas pin chip exists to prevent. */}
          {raw && (
            <button
              onClick={() => setRaw(null)}
              title="Clear the filter that was sent over and show the whole table again"
              style={{
                display: 'block', width: '100%', marginTop: 6, padding: '4px 6px', cursor: 'pointer',
                border: 'var(--hair)', background: 'rgba(255,170,0,.043)', color: '#FFF',
                fontSize: 'var(--t-micro)', fontFamily: 'var(--sans)', textAlign: 'left',
              }}
            >showing only {raw.label} ×</button>
          )}
          {/* Plants only: the Atlas draws geocoded plants, so handing it an account or contact
              filter would silently show a different population than the one on screen. */}
          {ent === 'fac' && onOpenInAtlas && res && res.rows.length > 0 && (
            <button
              onClick={() => onOpenInAtlas({
                f: 'id', o: 'in', v: res.rows.map((r) => r._key).join(','),
                label: `${N(res.rows.length)} plants sent over from the grid`,
              })}
              style={{
                width: '100%', marginTop: 6, padding: '5px 0', cursor: 'pointer',
                border: 'var(--hair)', background: 'transparent', color: 'var(--dim)',
                fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
              }}
            >Open this page in Atlas →</button>
          )}
        </Section>

        <Section label="Quick filters">
          {meta.derived[ent].map((t) => {
            const n = tags?.counts?.[t];
            const on = chip === t;
            return (
              <button
                key={t}
                onClick={() => setChip(on ? null : t)}
                style={{
                  display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8,
                  padding: '5px 8px', marginBottom: 2, cursor: 'pointer', textAlign: 'left',
                  border: 'var(--hair)', background: on ? 'rgba(255,170,0,.043)' : 'transparent',
                  color: on ? '#FFF' : 'var(--dim)', fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
                }}
              >
                <span>{t}</span>
                {/* A chip with no number is a guess about whether it is worth clicking. Several
                    drop to zero once another condition is on, which was invisible before. */}
                <span className="num" style={{ color: n === 0 ? 'var(--dim2)' : 'var(--am)' }}>
                  {n == null ? '' : N(n)}
                </span>
              </button>
            );
          })}
        </Section>

        {/* THE COLUMN PICKER. Grouped by the registry's own `grp`, searchable, and every row states
            where the column comes from — the provenance string is the point of the registry, and a
            column list without it is a list of names. */}
        <Section label={`Columns (${cols.length} of ${fields.length})`}>
          <button
            onClick={() => setPickerOpen(!pickerOpen)}
            style={{
              width: '100%', padding: '5px 0', cursor: 'pointer', border: 'var(--hair)',
              background: 'transparent', color: 'var(--dim)', fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
            }}
          >{pickerOpen ? 'done' : 'choose columns'}</button>
          {pickerOpen && (
            <div style={{ marginTop: 6 }}>
              <input
                placeholder="search 116 columns and their sources…"
                value={colq}
                onChange={(e) => setColq(e.target.value)}
                style={{
                  width: '100%', padding: '4px 6px', marginBottom: 6, border: 'var(--hair)',
                  background: 'var(--s1)', color: 'var(--ink)', fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
                }}
              />
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {fields
                  .filter((f) => !colq || (f.lbl + ' ' + f.k + ' ' + (f.src || '') + ' ' + (f.grp || '')).toLowerCase().includes(colq.toLowerCase()))
                  .map((f) => {
                    const on = cols.some((c) => c.k === f.k);
                    return (
                      <label key={f.k} title={f.src} style={{
                        display: 'flex', gap: 6, alignItems: 'baseline', padding: '3px 0',
                        borderBottom: 'var(--hair)', cursor: 'pointer', fontSize: 'var(--t-micro)',
                        color: on ? 'var(--ink)' : 'var(--dim)',
                      }}>
                        <input type="checkbox" checked={on} onChange={() => toggleCol(f.k)} />
                        <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.lbl}</span>
                        <span style={{ color: 'var(--dim2)' }}>{f.t}</span>
                      </label>
                    );
                  })}
              </div>
              {picked[ent] && picked[ent].length > 0 && (
                <button
                  onClick={() => setPicked({ ...picked, [ent]: [] })}
                  style={{
                    width: '100%', marginTop: 6, padding: '4px 0', cursor: 'pointer', border: 'var(--hair)',
                    background: 'transparent', color: 'var(--dim2)', fontSize: 'var(--t-micro)', fontFamily: 'var(--sans)',
                  }}
                >reset to the default nine</button>
              )}
            </div>
          )}
        </Section>

        <Section label={`Conditions (${conds.length})`}>
          {conds.map((c, i) => (
            <ConditionRow
              key={i} meta={meta} ent={ent} cond={c}
              onChange={(next) => setConds(conds.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setConds(conds.filter((_, j) => j !== i))}
            />
          ))}
          <button
            onClick={() => setConds([...conds, { f: fields[0].k, o: 'contains', v: '' }])}
            style={{
              width: '100%', padding: '5px 0', marginTop: 4, cursor: 'pointer',
              border: 'var(--hair)', background: 'transparent', color: 'var(--dim)',
              fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
            }}
          >+ condition</button>
        </Section>
      </aside>

      {/* ── canvas ───────────────────────────────────────────────────────────────────────────── */}
      <section style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>

        {/* Stat row: hairline-separated columns, not cards. Headline is --t-disp, label is
            --t-label (criteria C1, C2). */}
        <div style={{ display: 'flex', borderBottom: 'var(--hair)', flex: '0 0 auto' }}>
          {statCells(ent, res).map((c) => (
            <div key={c.label} style={{ padding: 'var(--gut)', borderRight: 'var(--hair)', minWidth: 128 }}>
              <div className="t-disp" style={{ color: 'var(--ink)' }}>{c.value}</div>
              <div className="t-label" style={{ marginTop: 4 }}>{c.label}</div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', padding: 'var(--gut)', alignSelf: 'center' }}>
            <span className="t-micro">{busy ? 'querying…' : res ? `${res.ms} ms` : ''}</span>
          </div>
        </div>

        {err && (
          <div style={{ padding: 'var(--gut)', color: 'var(--rd)', borderBottom: 'var(--hair)' }}>
            {/* Loud, not silent. An unknown column, a corrupt filter or an unparseable number all
                throw rather than quietly matching every row — that failure class is the reason this
                message exists at all. */}
            <div className="t-label" style={{ color: 'var(--rd)' }}>That filter did not run</div>
            <div style={{ marginTop: 6 }}>{err}</div>
          </div>
        )}

        <div style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--t-body)' }}>
            <thead>
              <tr>
                {cols.map((f) => (
                  <th
                    key={f.k}
                    title={f.src}
                    style={{
                      position: 'sticky', top: 0, zIndex: 1, background: 'var(--s3)',
                      borderBottom: 'var(--hair)', padding: '7px 10px',
                      textAlign: f.t === 'num' ? 'right' : 'left', whiteSpace: 'nowrap',
                      fontSize: 'var(--t-label)', letterSpacing: '.13em', textTransform: 'uppercase',
                      fontWeight: 600, color: 'var(--dim2)',
                    }}
                  >{f.lbl}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(res?.rows ?? []).map((r) => (
                /* Click opens the receipts drawer, which reads /api/row rather than this row: the
                   drawer needs EVERY field, not the nine on screen, and reading it off the grid row
                   is how a provenance panel ends up full of em-dashes. */
                <tr key={r._key} onClick={() => onOpenRow?.(ent, r._key)}
                  style={{ borderBottom: 'var(--hair)', cursor: onOpenRow ? 'pointer' : undefined }}>
                  {cols.map((f) => (
                    <td
                      key={f.k}
                      className={f.t === 'num' ? 'num' : undefined}
                      style={{ padding: '5px 10px', whiteSpace: 'nowrap', maxWidth: f.w, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {f.t === 'num' ? N(r[f.k]) : String(r[f.k] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {res && res.rows.length === 0 && !err && (
            <div style={{ padding: 'var(--gut)', color: 'var(--dim)' }}>No rows match that filter.</div>
          )}
        </div>

        {/* The receipt. The compiled WHERE clause, so what ran is visible rather than described —
            this dataset gets audited from the screen, not from a conversation. */}
        {res?.sql && (
          <div style={{ padding: '6px var(--gut)', borderTop: 'var(--hair)', flex: '0 0 auto' }}>
            <span className="t-label">ran</span>{' '}
            <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-micro)', color: 'var(--bl)' }}>{res.sql}</span>
          </div>
        )}
      </section>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: 'var(--gut)', borderBottom: 'var(--hair)' }}>
      <div className="t-label" style={{ marginBottom: 8 }}>{label}</div>
      {children}
    </div>
  );
}

/* One condition row, generated from the registry: the operator list comes from the column's TYPE,
   so a numeric column cannot be offered "contains" and an enum cannot be offered "starts with". */
function ConditionRow({ meta, ent, cond, onChange, onRemove }: {
  meta: Meta; ent: Entity; cond: Cond;
  onChange: (c: Cond) => void; onRemove: () => void;
}) {
  const field = meta.fields[ent].find((f) => f.k === cond.f);
  const ops = field ? meta.opsFor[field.t] : [];
  const valueless = ['empty', 'nempty', 'true', 'false'].includes(cond.o);
  const sel: React.CSSProperties = {
    width: '100%', marginBottom: 3, padding: '4px 6px',
    border: 'var(--hair)', background: 'var(--s1)', color: 'var(--ink)',
    fontSize: 'var(--t-body)', fontFamily: 'var(--sans)',
  };

  return (
    <div style={{ border: 'var(--hair)', padding: 6, marginBottom: 6, opacity: !valueless && !cond.v ? 0.55 : 1 }}>
      <select
        style={sel} value={cond.f}
        onChange={(e) => {
          const f = meta.fields[ent].find((x) => x.k === e.target.value)!;
          // Keep the operator only if the new column's type still offers it.
          const keep = meta.opsFor[f.t].includes(cond.o) ? cond.o : meta.opsFor[f.t][0];
          onChange({ ...cond, f: f.k, o: keep });
        }}
      >
        {meta.fields[ent].map((f) => <option key={f.k} value={f.k}>{f.lbl}</option>)}
      </select>

      <select style={sel} value={cond.o} onChange={(e) => onChange({ ...cond, o: e.target.value })}>
        {ops.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>

      {!valueless && (
        <input
          style={sel} value={cond.v} placeholder={field?.t === 'num' ? '500k, $1B, 1,000,000' : 'value'}
          onChange={(e) => onChange({ ...cond, v: e.target.value })}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
        {/* The provenance string, in the UI, at the point of use. This is what lets the dataset be
            audited from the screen instead of from a conversation. */}
        <span className="t-micro" style={{ color: 'var(--dim2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }} title={field?.src}>
          {field?.src}
        </span>
        <button onClick={onRemove} style={{ border: 'none', background: 'none', color: 'var(--dim2)', cursor: 'pointer', fontSize: 'var(--t-body)' }}>×</button>
      </div>
    </div>
  );
}

/* The aggregate over the WHOLE matched set, not the page. That distinction is the contract of a
   filter — it is what proved the LIKE -> ILIKE port returned identical results even while the first
   page differed. */
function statCells(ent: Entity, res: QueryResult | null) {
  const a = res?.agg ?? {};
  const cell = (label: string, v: unknown) => ({ label, value: v == null ? '—' : Number(v).toLocaleString('en-US') });
  if (ent === 'acct') return [cell('accounts', a.rows), cell('in a buying role', a.buyers), cell('reachable', a.reach), cell('plants', a.plants), cell('full committee', a.full_committee)];
  if (ent === 'fac') return [cell('plants', a.rows), cell('reachable', a.reach), cell('accounts', a.accounts), cell('full committee', a.full_committee)];
  return [cell('contacts', a.rows), cell('with email', a.emails), cell('with phone', a.phones), cell('accounts', a.accounts), cell('decision makers', a.dm)];
}
